/**
 * ReactivityMapper — Audio source mapping UI for reactive layer settings.
 * For each setting marked as `reactive`, shows a dropdown to map it to an audio source.
 */

import { CustomLayerDefinition, AudioSource, LayerTypeSchema } from '../types';

const AUDIO_SOURCES: { value: AudioSource; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'bassEnergy', label: 'Bass' },
  { value: 'midEnergy', label: 'Mid' },
  { value: 'trebleEnergy', label: 'Treble' },
  { value: 'overallEnergy', label: 'Overall' },
  { value: 'beatIntensity', label: 'Beat' },
];

interface ReactivityMapperProps {
  layer: CustomLayerDefinition;
  schema: LayerTypeSchema;
  onUpdate: (key: string, source: string, intensity: number, smoothing: number) => void;
}

export default function ReactivityMapper({ layer, schema, onUpdate }: ReactivityMapperProps) {
  const reactiveSettings = schema.settings.filter(s => s.reactive);
  if (reactiveSettings.length === 0) return null;

  return (
    <div>
      <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-2">Audio Reactivity</label>
      <div className="space-y-2">
        {reactiveSettings.map(setting => {
          const mapping = layer.reactivity[setting.key] || { source: 'none', intensity: 0.5, smoothing: 0.15 };

          return (
            <div key={setting.key} className="bg-gray-800/40 rounded-lg p-2 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-300 w-20 truncate">{setting.label}</span>
                <span className="text-[10px] text-gray-500">reacts to</span>
                <select
                  value={mapping.source}
                  onChange={e => onUpdate(setting.key, e.target.value, mapping.intensity, mapping.smoothing)}
                  className="flex-1 bg-gray-700 rounded px-1.5 py-0.5 text-[10px] text-white border-none outline-none"
                >
                  {AUDIO_SOURCES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {mapping.source !== 'none' && (
                <div className="flex gap-3 pl-2">
                  <div className="flex-1">
                    <label className="text-[9px] text-gray-500 block">
                      Intensity: {mapping.intensity.toFixed(1)}
                    </label>
                    <input
                      type="range" min={0} max={1} step={0.1}
                      value={mapping.intensity}
                      onChange={e => onUpdate(setting.key, mapping.source, parseFloat(e.target.value), mapping.smoothing)}
                      className="w-full accent-purple-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[9px] text-gray-500 block">
                      Smoothing: {mapping.smoothing.toFixed(2)}
                    </label>
                    <input
                      type="range" min={0.01} max={0.5} step={0.01}
                      value={mapping.smoothing}
                      onChange={e => onUpdate(setting.key, mapping.source, mapping.intensity, parseFloat(e.target.value))}
                      className="w-full accent-purple-500"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
